/**
 * O estado do sistema.
 *
 * Antes isto só existia no terminal. A tabela `execucao` guardava 13 rodadas
 * e nenhuma superfície as mostrava; a `termo_busca` sabia quando cada palavra
 * foi varrida pela última vez e ninguém via. "O acervo está atualizado?" era
 * uma pergunta sem resposta na interface.
 *
 * O DESENHO (20/10, "reimaginar tudo"): os números em cartões; a cobertura de
 * cada termo como uma BARRA (quantas encontrou, comparado ao termo que mais
 * encontrou) com a última varredura ao lado; e as rodadas como linhas, as
 * mesmas da Extração. Eram duas tabelas.
 *
 * AS PEÇAS DO DESIGN SYSTEM (20/10): as cifras (com a ajuda escrita no toque),
 * as seções, a barra e o aviso saem de `src/ds`. Em `gestao.module.css` ficou
 * só a grade de três colunas de cada palavra (o termo, a barra, a última
 * busca).
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/cliente";
import { AreaConfig } from "../componentes/AreaConfig";
import { Aviso, Barra, Cifras, Secao } from "../ds";
import { ListaDeRodadas } from "./ListaDeRodadas";
import css from "./gestao.module.css";

function quando(iso?: string | null) {
  if (!iso) return "nunca buscada";
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (dias <= 0) return "buscada hoje";
  if (dias === 1) return "buscada ontem";
  return `buscada há ${dias} dias`;
}

export function AcervoTela() {
  const { data, isPending } = useQuery({ queryKey: ["acervo"], queryFn: api.acervo });
  if (isPending || !data) {
    return (
      <AreaConfig titulo="Base de licitações">
        <Aviso simples>Carregando…</Aviso>
      </AreaConfig>
    );
  }

  const numeros = [
    { rotulo: "Licitações encontradas", valor: data.licitacoes, ajuda: "Tudo que as buscas nos portais já encontraram." },
    {
      rotulo: "Editais enviados à mão",
      valor: data.enviados,
      ajuda: "Editais que alguém trouxe de fora. Ficam fora dos outros números desta tela, que medem o alcance da coleta.",
    },
    {
      rotulo: "Barradas por palavra excluída",
      valor: data.vetadas,
      ajuda: "Derrubadas pela etapa 3 — o objeto casa um termo negativo, e elas não chegam a custar chamada de IA.",
    },
    { rotulo: "Leituras da IA", valor: data.analises, ajuda: "Vezes que a IA leu uma licitação, somando a triagem e a leitura do edital." },
    { rotulo: "Editais lidos por inteiro", valor: data.profundas, ajuda: "As que tiveram o edital lido do começo ao fim." },
    { rotulo: "Editais guardados", valor: data.editais_em_disco, ajuda: "PDFs de edital guardados no sistema agora." },
    {
      rotulo: "Editais que o portal não liberou",
      valor: data.editais_indisponiveis,
      ajuda: "A fonte externa não permitiu o download; a IA julgou pelos metadados.",
    },
  ];

  const termos = [...data.termos].sort((a, b) => b.total_encontrado - a.total_encontrado);
  const maior = Math.max(1, ...termos.map((t) => t.total_encontrado));

  return (
    <AreaConfig
      titulo="Base de licitações"
      descricao={`Tudo o que as buscas nos portais já encontraram para o ${data.organizacao}, e quando cada palavra de busca rodou pela última vez.`}
    >
      {/* AS CIFRAS SOLTAS (20/10, "minimalista"): o número grande e o nome
          embaixo, sem caixa nenhuma; o zero fica apagado. A ajuda é o title
          onde há mouse; no toque, é o único lugar onde "vetadas" ganha
          explicação, e por isso aparece escrita. */}
      <Cifras itens={numeros} />

      <Secao
        titulo="Quanto cada palavra de busca encontrou"
        conta={termos.length}
        nota={
          <>
            A linha mostra quantas licitações a palavra trouxe, comparada à que mais
            trouxe. Palavra fora do perfil continua na base, mas não é buscada de novo.
          </>
        }
      >
        {/* A BARRA COM O NÚMERO NA PONTA: o termo, uma linha fina do
            tamanho do que ele encontrou e o número logo depois dela; a última
            varredura apagada à direita. Sem caixa e sem cabeçalho. */}
        <ul className={css.barras}>
          {termos.map((t) => (
            <li key={t.termo} data-fraco={!t.no_perfil}>
              <span className={css.barraTermo}>
                {t.termo}
                {!t.no_perfil && <small> · fora do perfil</small>}
              </span>
              <span className={css.barraTrilho}>
                <Barra fracao={t.total_encontrado / maior} valor={t.total_encontrado.toLocaleString("pt-BR")} />
              </span>
              <span className={css.barraQuando} title={t.ultima_varredura ?? ""}>
                {quando(t.ultima_varredura)}
              </span>
            </li>
          ))}
        </ul>
      </Secao>

      <Secao titulo="Últimas buscas nos portais">
        <ListaDeRodadas linhas={data.rodadas_recentes} comQuem />
      </Secao>
    </AreaConfig>
  );
}
